import { DomainEntity, Id, Json } from "@homeleite/arch";
import { EMPTY } from "@homeleite/common-utils";
import { ProducerSummary, CompanySummary, BreedSummary } from "@homeleite/domain";
import { AuditData } from '../animals/auditData';

export class BreederSummary implements DomainEntity {
    constructor(
        public readonly id = Id.gen(),
        public apcId: string = EMPTY,
        public name: string = EMPTY,
        public register: string = EMPTY,
        /** Código de central / NAAB (ex.: 29HO12345). */
        public centralCode: string = EMPTY,
        public nickName: string = EMPTY,
        public birth: Date = null,
        public company: CompanySummary = null,
        public breed: BreedSummary = null,
        public isActive = true
    ) { }

    /** Ex.: "Reg. AX156187 · NAAB 29HO12345" — vazio se não houver códigos. */
    get codesLabel(): string {
        const parts: string[] = [];
        const reg = (this.register || '').trim();
        const naab = (this.centralCode || '').trim();
        if (reg) {
            parts.push(`Reg. ${reg}`);
        }
        if (naab) {
            parts.push(`NAAB ${naab}`);
        }
        return parts.join(' · ');
    }

    toJson(): any {
        const birthDate = this.birth instanceof Date ? this.birth : (this.birth ? new Date(this.birth) : null);

        return Json.to.simple(this, {
            birth: birthDate?.toISOString() ?? null,
        });
    }

    static fromJson(json: any): BreederSummary {
        if (!json) {
            return null;
        }
        // API: nickName + birthdate; Mongo legado / full: nickname + birth
        const nick = json.nickName ?? json.nickname ?? EMPTY;
        const birthRaw = json.birth ?? json.birthdate;
        return Json.from.simple(json, BreederSummary, {
            nickName: nick,
            name: json.name ?? EMPTY,
            register: json.register ?? EMPTY,
            centralCode: json.centralCode ?? json.naab ?? EMPTY,
            birth: birthRaw ? new Date(birthRaw) : null,
            company: json.company ? CompanySummary.fromJson(json.company) : null,
            breed: json.breed ? BreedSummary.fromJson(json.breed) : null,
        });
    }

    static createBy(
        breeder: Breeder,
    ): BreederSummary {
        return new BreederSummary(
            breeder.id,
            breeder.apcId,
            breeder.name,
            breeder.register,
            breeder.centralCode,
            breeder.nickName,
            breeder.birth,
            breeder.company,
            breeder.breed,
            breeder.isActive
        );
    }
}

export class Breeder implements DomainEntity {
	constructor(
		public readonly id = Id.gen(),
		public apcId: string = null,
		public name: string = EMPTY,
		public nickName: string = EMPTY,
        public birth: Date = null,
        public register: string = EMPTY,
		public centralCode: string = EMPTY,
        public internationalRegister: string = EMPTY,
		public variety = null,
		public blood: string = EMPTY,
        public producer: ProducerSummary = null,
        public company: CompanySummary = null,
        public breed: BreedSummary = new BreedSummary(),
        public auditData: AuditData = null,
        public isActive = true
	) { }

	toJson(): any {
        const birthDate = this.birth instanceof Date ? this.birth : (this.birth ? new Date(this.birth) : null);

		return Json.to.simple(this, {
            birth: birthDate?.toISOString() ?? null,
            producer: this.producer?.toJson() ?? null,
            company: this.company?.toJson() ?? null,
            breed: this.breed?.toJson() ?? null,
            auditData: this.auditData?.toJson() ?? null
        });
	}

	static fromJson(json: any): Breeder {
		if (!json) {
			return null;
		}
		const nick = json.nickName ?? json.nickname ?? EMPTY;
		const birthRaw = json.birth ?? json.birthdate;
		return Json.from.simple(json, Breeder, {
            nickName: nick,
            name: json.name ?? EMPTY,
            birth: birthRaw ? new Date(birthRaw) : null,
            producer: json.producer ? ProducerSummary.fromJson(json.producer) : null,
            company: json.company ? CompanySummary.fromJson(json.company) : null,
            breed: json.breed ? BreedSummary.fromJson(json.breed) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null
        });
	}

    static createBy(
        breeder: Breeder,
        id: string,
        producerSummary: ProducerSummary
    ): Breeder {
        return new Breeder(
            id,
            breeder.apcId,
            breeder.name,
            breeder.nickName,
            breeder.birth,
            breeder.register,
            breeder.centralCode,
            breeder.internationalRegister,
            breeder.variety,
            breeder.blood,
            producerSummary,
            breeder.company,
            breeder.breed,
            breeder.auditData,
            breeder.isActive
        );
    }
}
